import { type Dispatch, type SetStateAction, useEffect, useState } from "react";

import { FieldMultiSort } from "@/appComponents/Blocks/Composites/FieldMultiSort/FieldMultiSort";
import { Flex, isNullish, Search, Text } from "@theorgchartdev/storybook";
import { GetDoc } from "@/Utils/StateLib";
import { type FieldData, TabProps } from "./ExcelContent";
import type { FormController } from "@/hooks/useFormController";
import { appstr } from "@coreapp/utils/appstr";
import { QXDoc } from "@coreapp/CoreApp/QXDoc";
import { getEmployeePositionFields } from "../ExportModal.utils";
import { enumObjClassNames, kNameID, kPositionTitleID, kTitleID } from "@coreapp/constants";

// Maximum number of additional fields (beyond the locked mandatory columns) selectable when the
// Accessibility Report variant is enabled. The backend also enforces this cap on output. For
// position charts this is the COMBINED budget across the Employee and Position field tabs.
const MAX_ACCESSIBILITY_ADDITIONAL_FIELDS = 3;

type CategoryKey = "employee" | "position";

// When the budget is shared across the two position-chart field tabs, higher-priority categories
// fill first and the remaining tabs get whatever is left. A fixed order makes the ≤3 allocation
// deterministic regardless of which tab the user mounts/visits first.
const ACCESSIBILITY_CATEGORY_PRIORITY: CategoryKey[] = ["position", "employee"];

// Resolves the field NAMES (as they appear in the field lists) for this category's mandatory,
// always-exported columns. Field names can differ from their stable IDs (custom/localized), so
// they are looked up. Employee charts lock Name + Title; position charts split the mandatory
// columns across tabs — Holder Name on the Employee/Holder tab, Position Title on the Position tab.
const getLockedAccessibilityFieldNames = (
  doc: Nullish<QXDoc>,
  categoryKey: CategoryKey
): string[] => {
  if (isNullish(doc)) {
    return [];
  }

  const resolveName = (fieldClass: any, fieldId: string): string | undefined =>
    fieldClass?.GetFieldDefByID(fieldId)?.GetFieldName();

  if (doc.IsPositionDocument?.()) {
    if (categoryKey === "position") {
      const positionClass = doc.GetObjDB()?.GetClassByName(enumObjClassNames.position);
      return [resolveName(positionClass, kPositionTitleID)].filter(
        (fieldName): fieldName is string => Boolean(fieldName)
      );
    }
    return [resolveName(doc.GetBoxClass(), kNameID)].filter((fieldName): fieldName is string =>
      Boolean(fieldName)
    );
  }

  const boxClass = doc.GetBoxClass();
  return [kNameID, kTitleID]
    .map(fieldId => resolveName(boxClass, fieldId))
    .filter((fieldName): fieldName is string => Boolean(fieldName));
};

// Count of non-locked (additional) fields currently included in a category, per the durable form
// controller. Used to share the combined additional-field budget across tabs.
const countCategoryAdditional = (
  controller: FormController<FieldData>,
  doc: Nullish<QXDoc>,
  categoryKey: CategoryKey
): number => {
  const locked = new Set(getLockedAccessibilityFieldNames(doc, categoryKey));
  return controller.fieldData.categoryFields[categoryKey].included.filter(
    (field: string) => !locked.has(field)
  ).length;
};

const arraysEqual = (a: string[], b: string[]): boolean => {
  if (a.length !== b.length) {
    return false;
  }
  return a.every((value, index) => value === b[index]);
};

// Enforces the Accessibility Report invariant on a raw {Included, Excluded} selection:
//   - every locked field present in this category leads Included, in lockedNames order
//   - at most MAX_ACCESSIBILITY_ADDITIONAL_FIELDS non-locked fields stay Included
//   - overflow non-locked fields move to the front of Excluded, preserving relative order
//   - locked fields are removed from Excluded; no field appears in both columns
// Only call this when the Accessibility Report is enabled — it is not a no-op otherwise.
export const reconcileAccessibilityFields = (
  included: string[],
  excluded: string[],
  lockedNames: string[],
  allFieldNames: string[],
  maxAdditional: number = MAX_ACCESSIBILITY_ADDITIONAL_FIELDS
): { Included: string[]; Excluded: string[] } => {
  const availableSet = new Set(allFieldNames);
  const lockedSet = new Set(lockedNames);

  // Only lead with locked fields that actually belong to this category's available list. On a
  // position tab the Name/Title fields may be filtered out entirely, in which case the function
  // degrades to clamping additional fields — the backend force-includes Name/Title regardless.
  const presentLocked = lockedNames.filter(field => availableSet.has(field));

  const seen = new Set<string>();
  const additionalIncluded = included.filter(field => {
    if (lockedSet.has(field) || !availableSet.has(field) || seen.has(field)) {
      return false;
    }
    seen.add(field);
    return true;
  });

  const cap = Math.max(0, maxAdditional);
  const keptAdditional = additionalIncluded.slice(0, cap);
  const overflow = additionalIncluded.slice(cap);

  const nextIncluded = [...presentLocked, ...keptAdditional];
  const includedSet = new Set(nextIncluded);

  const restSeen = new Set<string>();
  const restExcluded = excluded.filter(field => {
    if (lockedSet.has(field) || !availableSet.has(field) || includedSet.has(field)) {
      return false;
    }
    if (restSeen.has(field)) {
      return false;
    }
    restSeen.add(field);
    return true;
  });

  const overflowSet = new Set(overflow);
  const nextExcluded = [...overflow, ...restExcluded.filter(field => !overflowSet.has(field))];

  return { Excluded: nextExcluded, Included: nextIncluded };
};

export const ExcelFields = (props: TabProps) => {
  const [queryString, setQueryString] = useState("");
  // True while a pointer drag is in progress over the field lists. The accessibility cap is
  // clamped only once the drag settles (see the reconcile effect), so dnd-kit fully owns the
  // transient mid-drag state and no field is lost on drop.
  const [isDragging, setIsDragging] = useState(false);

  const isAccessibilityReport = props.formController.fieldData.accessibilityReport;

  const getFilteredAllFieldNames = () => {
    return getAllFieldNames(props).filter(
      (fieldName: string) => !props.fieldFilter || props.fieldFilter(fieldName)
    );
  };

  // Additional-field budget remaining for THIS tab. The cap is a combined total across the
  // position-chart field tabs; higher-priority categories claim their share first, so this tab
  // gets whatever is left. Employee charts have only the "employee" category populated, so the
  // budget is always the full cap — preserving the original single-tab behavior.
  const getRemainingAdditionalBudget = () => {
    const doc = GetDoc();
    let claimedByHigherPriority = 0;
    for (const category of ACCESSIBILITY_CATEGORY_PRIORITY) {
      if (category === props.categoryKey) {
        break;
      }
      claimedByHigherPriority += countCategoryAdditional(props.formController, doc, category);
    }
    return Math.max(
      0,
      MAX_ACCESSIBILITY_ADDITIONAL_FIELDS -
        Math.min(claimedByHigherPriority, MAX_ACCESSIBILITY_ADDITIONAL_FIELDS)
    );
  };

  // Funnels raw {Included, Excluded} state through the accessibility invariant. No-op when the
  // Accessibility Report is off, so the non-accessibility path is unchanged.
  const applyAccessibilityReconcile = (state: { Included: string[]; Excluded: string[] }) => {
    if (!isAccessibilityReport) {
      return state;
    }
    return reconcileAccessibilityFields(
      state.Included,
      state.Excluded,
      getLockedAccessibilityFieldNames(GetDoc(), props.categoryKey),
      getFilteredAllFieldNames(),
      getRemainingAdditionalBudget()
    );
  };

  const [fieldNames, setFieldNames] = useState(() => {
    const categoryState = props.formController.fieldData.categoryFields[props.categoryKey];
    const allFieldNames = getFilteredAllFieldNames();
    const availableFieldSet = new Set(allFieldNames);
    const savedIncluded = categoryState.included.filter((f: string) => availableFieldSet.has(f));
    const savedExcluded = categoryState.excluded.filter((f: string) => availableFieldSet.has(f));
    const savedIncludedSet = new Set(savedIncluded);

    const initial =
      savedExcluded.length > 0 || savedIncluded.length > 0
        ? {
            Excluded: allFieldNames.filter((f: string) => !savedIncludedSet.has(f)),
            Included: savedIncluded
          }
        : // Neither saved — all included by default
          { Excluded: [] as string[], Included: allFieldNames };

    // A stale saved selection (e.g. Name/Title excluded) is corrected here; the write-back to the
    // form controller happens in the accessibility effect's mount run, since useState initializers
    // must not have side effects.
    return applyAccessibilityReconcile(initial);
  });

  const updateFormController = (includedFields: string[], excludedFields: string[]) => {
    const currentCategoryFields = props.formController.fieldData.categoryFields;
    props.formController.onFieldChange("categoryFields", {
      ...currentCategoryFields,
      [props.categoryKey]: { included: includedFields, excluded: excludedFields }
    });
  };

  // Accepts MultiSort's raw {Included, Excluded} as-is. During an active drag, dnd-kit commits
  // transient intermediate states here on every pointer move (it moves the dragged item between
  // columns mid-drag); the accessibility cap is NOT enforced here because relocating the dragged
  // item out of Included mid-drag desyncs dnd-kit's internal tracking and makes fields vanish on
  // drop. The cap is instead clamped once the drop settles — see the reconcile effect below.
  const handleFieldsChange: Dispatch<SetStateAction<typeof fieldNames>> = dispatch => {
    const nextState = typeof dispatch === "function" ? dispatch(fieldNames) : dispatch;
    setFieldNames(nextState);
    updateFormController(nextState.Included, nextState.Excluded);
  };

  const handleToggleAll = (target: "Included" | "Excluded") => () => {
    const allFieldNames = getFilteredAllFieldNames();
    const isIncludeAll = target === "Included";

    const nextState = applyAccessibilityReconcile({
      Excluded: isIncludeAll ? [] : allFieldNames,
      Included: isIncludeAll ? allFieldNames : []
    });

    setFieldNames(nextState);
    updateFormController(nextState.Included, nextState.Excluded);
  };

  // While a drag is in progress, a global pointer-release ends it. dnd-kit's own drop handler
  // and this reset both run on the same pointerup event, so React 18 batches the settled field
  // state and isDragging=false into one render — the reconcile effect below then sees the final,
  // fully-committed selection. Listening only while dragging keeps the window unbound otherwise.
  useEffect(() => {
    if (!isDragging) {
      return;
    }
    const endDrag = () => setIsDragging(false);
    window.addEventListener("pointerup", endDrag);
    window.addEventListener("pointercancel", endDrag);
    return () => {
      window.removeEventListener("pointerup", endDrag);
      window.removeEventListener("pointercancel", endDrag);
    };
  }, [isDragging]);

  // Enforces the accessibility invariant on the SETTLED selection: re-includes locked Name/Title
  // fields and clamps additional fields to the cap, moving overflow back to Excluded. It runs on
  // mount, when the report is toggled on, and after every committed field change — but never mid
  // drag (isDragging), because relocating the dragged item before dnd-kit finishes the drop makes
  // fields disappear. Local state may already be reconciled by the initializer, but the durable
  // form controller — which outlives this component across tab switches and feeds the Export
  // button's enabled state — can still hold a stale/over-cap selection, so its write-back is
  // guarded independently. The equality guards avoid redundant writes and reconcile is idempotent,
  // so the fieldNames dependency cannot loop.
  useEffect(() => {
    if (!isAccessibilityReport || isDragging) {
      return;
    }
    const reconciled = applyAccessibilityReconcile(fieldNames);
    if (
      !arraysEqual(reconciled.Included, fieldNames.Included) ||
      !arraysEqual(reconciled.Excluded, fieldNames.Excluded)
    ) {
      setFieldNames(reconciled);
    }
    const saved = props.formController.fieldData.categoryFields[props.categoryKey];
    if (
      !arraysEqual(reconciled.Included, saved.included) ||
      !arraysEqual(reconciled.Excluded, saved.excluded)
    ) {
      updateFormController(reconciled.Included, reconciled.Excluded);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAccessibilityReport, isDragging, fieldNames]);

  const handleInput = (query: string) => {
    setQueryString(query);
  };

  const getFilteredFieldNames = () => {
    if (!queryString) {
      return fieldNames;
    }

    const lowerCaseQuery = queryString.toLowerCase();
    const filterFields = (fieldNames: string[]) => {
      return fieldNames.filter(fieldName => fieldName.toLowerCase().includes(lowerCaseQuery));
    };

    return {
      Excluded: filterFields(fieldNames.Excluded),
      Included: filterFields(fieldNames.Included)
    };
  };

  // When the Accessibility Report is enabled, the mandatory columns are locked selections that
  // always export and do not count toward the additional-field cap. The cap is a COMBINED total
  // across the position-chart field tabs, so it sums this tab's additional fields with the other
  // category's (from the durable form controller). Once reached, the remaining excluded fields are
  // disabled here — and, because every tab reads the same shared count, in the other tab too.
  const getAccessibilityCap = () => {
    if (!isAccessibilityReport) {
      return { disabledIds: undefined, isCapReached: false };
    }

    const doc = GetDoc();
    const lockedFieldNames = new Set(getLockedAccessibilityFieldNames(doc, props.categoryKey));
    const currentAdditionalCount = fieldNames.Included.filter(
      (field: string) => !lockedFieldNames.has(field)
    ).length;
    const otherAdditionalCount = (["position", "employee"] as CategoryKey[])
      .filter(category => category !== props.categoryKey)
      .reduce(
        (total, category) => total + countCategoryAdditional(props.formController, doc, category),
        0
      );
    const isCapReached =
      currentAdditionalCount + otherAdditionalCount >= MAX_ACCESSIBILITY_ADDITIONAL_FIELDS;

    // Locked fields are always disabled so they cannot be dragged out of Included. Once the cap
    // is reached, every remaining excluded field is disabled to block further additions.
    const disabledIds = [
      ...fieldNames.Included.filter((field: string) => lockedFieldNames.has(field)),
      ...(isCapReached ? fieldNames.Excluded : [])
    ];

    return { disabledIds, isCapReached };
  };

  const { disabledIds, isCapReached } = getAccessibilityCap();

  const capMessageId = `excel-accessibility-cap-message-${props.categoryKey}`;
  const capMessage = isCapReached
    ? appstr("accessibilityFieldCapReached")
    : appstr("Maximum 3 additional fields");

  const [announcedCapMessage, setAnnouncedCapMessage] = useState("");
  useEffect(() => {
    setAnnouncedCapMessage(isAccessibilityReport ? capMessage : "");
  }, [isAccessibilityReport, capMessage]);

  return (
    <Flex height={"100%"} direction="column" gap="8px" padding="6px 24px 0">
      <Search size="medium" placeholder={appstr("findfield")} onInput={handleInput} />
      <Flex padding={"6px"}>
        <Text color="grey" size="xsmall">
          {appstr(
            "Choose which fields to include in the order in which they will appear. Drag fields from one column to the other."
          )}
        </Text>
      </Flex>
      {isAccessibilityReport && (
        <Flex padding={"0 6px"}>
          <Text
            id={capMessageId}
            color={isCapReached ? "warning" : "grey"}
            size="xsmall"
            role="status"
            aria-live="polite"
            aria-atomic="true"
          >
            {announcedCapMessage}
          </Text>
        </Flex>
      )}
      <Flex
        flex={1}
        width="100%"
        overflow="hidden"
        divProps={
          isAccessibilityReport
            ? {
                role: "group",
                "aria-label": appstr("Fields"),
                "aria-describedby": capMessageId,
                // Capture phase so we register the drag before dnd-kit's own handlers; the
                // matching pointer-release listener (bound while dragging) clears it, gating the
                // cap clamp to the settled drop rather than the mid-drag intermediate states.
                onPointerDownCapture: () => setIsDragging(true)
              }
            : undefined
        }
      >
        <FieldMultiSort
          dataTestIdPrefix="export-modal-excel-fields"
          hasFilter={queryString.length > 0}
          onToggleAll={handleToggleAll}
          onChange={handleFieldsChange}
          filteredItems={getFilteredFieldNames()}
          items={fieldNames}
          disabledIds={disabledIds}
          disableToggleAll={isAccessibilityReport}
        />
      </Flex>
    </Flex>
  );
};

const getAllFieldNames = (props: TabProps) => {
  const doc: Nullish<QXDoc> = GetDoc();
  if (isNullish(doc)) {
    return [];
  }

  const excelKeys = doc.GetExcelKeys();
  if (!isNullish(excelKeys)) {
    return excelKeys;
  }

  if (doc.IsPositionDocument()) {
    const { keys, employeeKeys } = getEmployeePositionFields(doc);
    const categoryAvailableFields = props.categoryKey === "position" ? keys : employeeKeys;
    const availableFieldSet = new Set(categoryAvailableFields);

    const categoryState = props.formController.fieldData.categoryFields[props.categoryKey];
    const allSavedFields = [...categoryState.included, ...categoryState.excluded];
    const savedFieldsStillAvailable = allSavedFields.filter(field => availableFieldSet.has(field));

    if (savedFieldsStillAvailable.length > 0) {
      const savedFieldSet = new Set(savedFieldsStillAvailable);
      const newFields = categoryAvailableFields.filter(
        (field: string) => !savedFieldSet.has(field)
      );
      return [...new Set(savedFieldsStillAvailable), ...new Set(newFields)];
    }

    return Array.from(new Set(categoryAvailableFields));
  }

  return doc.GetFieldList2(true, false, false, false).map((item: any) => item.GetFieldName() ?? "");
};
